import { ReactNode } from "react";

/**
 * Shared row model for grouped views. A grouped table is a two-level nesting
 * tree: group-header rows whose children are the group's item rows plus
 * synthetic rows: skeletons render a shimmer bar per column, while load-more
 * and error rows don't map onto columns and render full-width. The
 * `useGroupedView` hook builds this shape and feeds it to `ViewTable`'s
 * nesting.
 */

export interface GroupDescriptor {
    /** Stable across refetches, e.g. `plaid:12`, `client:<uuid>`, `client:unassigned`. */
    key: string;
    label: string;
    iconUrl?: string | null;
    icon?: ReactNode;
    /** Full item count in the group (drives the muted "(12)" and hasMore math). */
    totalCount: number;
}

/**
 * Per-group loading state owned by the data layer (react-query). Absent state
 * means "not loaded yet" — the group shows skeletons once expanded.
 */
export interface GroupChildrenState<TItem> {
    items: TItem[];
    hasMore: boolean;
    isLoadingFirstPage: boolean;
    isLoadingMore: boolean;
    isError: boolean;
    /** First page has loaded successfully — an empty `items` is final, not a transient tick. */
    isSettled: boolean;
}

interface GroupByRowBase<TItem> {
    id: string;
    // Only group-header rows carry children; declared on the base so
    // `childrenKey: "children"` is a valid `keyof` the union for ViewTable.
    children?: Array<GroupChildRow<TItem>>;
}

export interface GroupHeaderRow<TItem> extends GroupByRowBase<TItem> {
    rowType: "groupHeader";
    descriptor: GroupDescriptor;
    children: Array<GroupChildRow<TItem>>;
}

export interface ItemRow<TItem> extends GroupByRowBase<TItem> {
    rowType: "item";
    item: TItem;
}

export interface LoadMoreRow<TItem> extends GroupByRowBase<TItem> {
    rowType: "loadMore";
    groupKey: string;
    isLoadingMore: boolean;
    remainingCount: number;
}

export interface SkeletonRow<TItem> extends GroupByRowBase<TItem> {
    rowType: "skeleton";
    groupKey: string;
}

export interface LoadErrorRow<TItem> extends GroupByRowBase<TItem> {
    rowType: "loadError";
    groupKey: string;
}

export type GroupChildRow<TItem> =
    | ItemRow<TItem>
    | LoadMoreRow<TItem>
    | SkeletonRow<TItem>
    | LoadErrorRow<TItem>;

export type GroupByRow<TItem> = GroupHeaderRow<TItem> | GroupChildRow<TItem>;

/**
 * Item rows are selectable; group-header rows are selectable so their checkbox
 * cascades to loaded children; synthetic rows never are.
 */
export const canSelectGroupRow = <TItem>(row: GroupByRow<TItem>): boolean =>
    row.rowType === "item" || row.rowType === "groupHeader";

/**
 * Only item rows are selectable. Prefer this over `canSelectGroupRow` when
 * children are fetched lazily per group: a header checkbox can only ever
 * cascade to the pages already loaded, so it reads as "select all" while
 * silently leaving the rest of the group out.
 */
export const canSelectItemRow = <TItem>(row: GroupByRow<TItem>): boolean =>
    row.rowType === "item";

export const GROUP_ROW_ID_PREFIX = "group:";

export const groupKeyFromRowId = (rowId: string): string | null =>
    rowId.startsWith(GROUP_ROW_ID_PREFIX)
        ? rowId.slice(GROUP_ROW_ID_PREFIX.length)
        : null;

const ITEM_ROW_ID_PREFIX = "item:";

/**
 * The table's row id for an item, which is namespaced so it can't collide with
 * a group or synthetic row. Anything matching a selection back to an item must
 * go through here — a raw item id will never match what the table stores.
 */
export const itemRowId = (rawItemId: string): string =>
    `${ITEM_ROW_ID_PREFIX}${rawItemId}`;
